import Footer from '../home/components/Footer';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useEffect } from 'react';

export default function StateOfAI() {
  useAnalytics('State of AI Safety Report');

  useEffect(() => {
    // Set document title
    document.title = 'State of AI Regulation 2025 | Constitutional AI Policy Institute';
    
    // Set meta tags
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'The crisis of inconsistency: Federal preemption vs state sovereignty in AI regulation. Discover the Third Way approach using Constitutional AI as a federal product safety standard.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'The crisis of inconsistency: Federal preemption vs state sovereignty in AI regulation. Discover the Third Way approach using Constitutional AI as a federal product safety standard.';
      document.head.appendChild(meta);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'AI regulation 2025, federal AI policy, state AI laws, California AI law, Constitutional AI, NIST AI RMF, AI preemption');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'AI regulation 2025, federal AI policy, state AI laws, California AI law, Constitutional AI, NIST AI RMF, AI preemption';
      document.head.appendChild(meta);
    }

    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://yourdomain.com/state-of-ai');
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', 'https://yourdomain.com/state-of-ai');
      document.head.appendChild(canonical);
    }

    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'State of AI Regulation 2025: The Crisis of Inconsistency');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'State of AI Regulation 2025: The Crisis of Inconsistency';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Navigating federal preemption, state sovereignty, and civil rights in AI regulation');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Navigating federal preemption, state sovereignty, and civil rights in AI regulation';
      document.head.appendChild(meta);
    }

    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) {
      ogType.setAttribute('content', 'article');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:type');
      meta.content = 'article';
      document.head.appendChild(meta);
    }

    const articleAuthor = document.querySelector('meta[property="article:author"]');
    if (articleAuthor) {
      articleAuthor.setAttribute('content', 'Alberto Rocha');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'article:author');
      meta.content = 'Alberto Rocha';
      document.head.appendChild(meta);
    }

    const articlePublished = document.querySelector('meta[property="article:published_time"]');
    if (articlePublished) {
      articlePublished.setAttribute('content', '2025-11-26');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'article:published_time');
      meta.content = '2025-11-26';
      document.head.appendChild(meta);
    }

    // Add Article schema markup
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "STATE OF AI REGULATION 2025: The Crisis of Inconsistency",
      "description": "Navigating the collision between federal preemption, state sovereignty, and civil rights in AI regulation. A Third Way approach using Constitutional AI.",
      "author": {
        "@type": "Person",
        "name": "Alberto Rocha",
        "jobTitle": "Director",
        "affiliation": {
          "@type": "Organization",
          "name": "Algorithmic Consistency Initiative, LLC"
        }
      },
      "publisher": {
        "@type": "Organization",
        "name": "Algorithmic Consistency Initiative, LLC",
        "url": "https://AlgorithmicConsistency.org"
      },
      "datePublished": "2025-11-26",
      "dateModified": "2025-11-26",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://yourdomain.com/state-of-ai"
      },
      "keywords": ["AI regulation", "federal preemption", "state AI laws", "Constitutional AI", "NIST AI RMF", "AI policy", "California AI law"]
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        
        <article className="container mx-auto px-6 lg:px-12 py-20 max-w-4xl">
          {/* Breadcrumb Navigation */}
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-slate-600">
              <li><a href="/" className="hover:text-red-800 cursor-pointer">Home</a></li>
              <li><span className="mx-2">/</span></li>
              <li><a href="/publications" className="hover:text-red-800 cursor-pointer">White Papers</a></li>
              <li><span className="mx-2">/</span></li>
              <li className="text-slate-900 font-medium">State of AI Regulation 2025</li>
            </ol>
          </nav>

          {/* Header */}
          <header className="mb-16 text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              State of AI Safety Report 2024
            </h1>
            <p className="text-xl text-slate-600 mb-4">
              The Crisis of Inconsistency in Federal and State AI Policy
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-slate-600 mt-6">
              <span>By <strong>Alberto Rocha</strong></span>
              <span>•</span>
              <span>Updated: January 15, 2025</span>
            </div>
          </header>

          {/* Author Bio Box */}
          <section className="mb-12 bg-slate-50 border-l-4 border-red-800 p-8">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-slate-200 overflow-hidden shadow-lg">
                  <img 
                    src="https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/a1d6cd473935f3227c013b1c4cb2ddef.png"
                    alt="Alberto Rocha"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900 mb-2">About the Author</h3>
                <p className="text-lg font-semibold text-red-800 mb-3">Alberto Rocha, Director</p>
                <p className="text-slate-700 leading-relaxed mb-3">
                  Researcher and author of <strong>"The Mirror Effect: How AI's Consistency Exposes the Flaw in Human Moral Preference."</strong> Author of <strong>19 books on AI</strong> and host of the <strong>200-episode podcast "AI and Us: Exploring Our Future."</strong> A <strong>Congressional appointee</strong> with <strong>40 years of experience</strong> in technology and policy, Rocha is a passionate advocate for algorithmic accountability and ethical AI governance.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-slate-900 text-white px-3 py-1 text-xs font-semibold whitespace-nowrap">Congressional Appointee</span>
                  <span className="bg-red-800 text-white px-3 py-1 text-xs font-semibold whitespace-nowrap">19 Books Published</span>
                  <span className="bg-slate-900 text-white px-3 py-1 text-xs font-semibold whitespace-nowrap">40 Years Experience</span>
                </div>
              </div>
            </div>
          </section>

          {/* Key Takeaways Box */}
          <div className="bg-red-50 border-l-4 border-red-800 p-6 mb-12">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
              <i className="ri-lightbulb-line text-red-800 mr-2"></i>
              Key Takeaways
            </h2>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                <span><strong>Regulatory Fracture:</strong> Federal preemption threatens state AI safety laws, creating uncertainty for developers and users.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                <span><strong>Deregulation Fails:</strong> Unconstrained AI models mirror historical human bias—they are not neutral but defective by design.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                <span><strong>Third Way Solution:</strong> Constitutional AI provides a federal product safety standard that satisfies both innovation and safety needs.</span>
              </li>
              <li className="flex items-start">
                <i className="ri-arrow-right-s-line text-red-800 mt-1 mr-2"></i>
                <span><strong>NIST RMF Update:</strong> Congress should mandate Constitutional Alignment as a technical standard for high-risk AI systems.</span>
              </li>
            </ul>
          </div>

          {/* Executive Summary */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Executive Summary</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              As we enter 2026, the United States faces a regulatory fracture. The incoming Administration's draft Executive Order seeking to preempt state AI laws has created a vacuum of uncertainty. We are caught between a "Wild West" federal approach (prioritizing speed) and a "Patchwork" state approach (prioritizing safety).
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              This White Paper argues that neither approach is sustainable. "Deregulation" ignores the product defects of AI (hallucinations and bias), while "State Patchworks" stifle innovation. We propose a Third Way: A Federal Product Safety Standard based on <a href="/constitutional-ai" className="text-red-800 hover:text-red-900 underline cursor-pointer">Constitutional AI</a>—replacing ideological debates with engineering constraints.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">1. The Current Landscape: A House Divided</h2>
            
            <h3 className="text-2xl font-bold text-slate-900 mb-4">The Federal Pivot: "Dominance over Safety"</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              The 2025 "America's AI Action Plan" and the draft Executive Order on "Eliminating State Law Obstruction" signal a definitive shift. The federal government is moving to:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-lg text-slate-700 ml-4">
              <li><strong>Preempt State Laws:</strong> Threatening to withhold broadband funding from states like California and Colorado that enforce AI safety rules.</li>
              <li><strong>Ban "DEI" in Code:</strong> Instructing the OMB to reject models with "ideological biases," effectively politicizing the concept of algorithmic fairness.</li>
              <li><strong>Goal:</strong> To outpace China by removing "burdensome" guardrails.</li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">The State Counter-Move: "The California Effect"</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              Despite the veto of SB 1047, California remains the de facto global regulator. With federal oversight retreating, states are stepping in to enforce:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-lg text-slate-700 ml-4">
              <li><strong>Transparency:</strong> Mandating disclosure of training data.</li>
              <li><strong>Civil Rights:</strong> Applying existing anti-discrimination laws (Unruh Act) to algorithmic decision-making.</li>
            </ul>
            <p className="text-lg text-slate-700 leading-relaxed">
              <strong>The Risk:</strong> A constitutional crisis where AI developers are frozen by conflicting mandates—sued by the Feds if they do filter for bias, and sued by the States if they don't.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">2. The Engineering Reality: Why "Deregulation" Fails</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              The federal push for "Minimal Regulation" is based on a misunderstanding of how Large Language Models (LLMs) work. It assumes that an unconstrained model is "neutral."
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">The Mirror Effect:</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              Our research demonstrates that unconstrained models are not neutral. They are <a href="/whitepaper" className="text-red-800 hover:text-red-900 underline cursor-pointer">Mirrors of historical human data</a>.
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-lg text-slate-700 ml-4">
              <li>Human data is full of "Decision Fatigue," "Cognitive Bias," and "Historical Discrimination."</li>
              <li>Therefore, a "deregulated" AI does not produce objective truth; it automates human error. It creates a product that is legally defective by design.</li>
            </ul>
            <p className="text-lg text-slate-700 leading-relaxed font-semibold">
              Conclusion: You cannot achieve "AI Dominance" with defective software. Safety constraints are not "woke"; they are quality control.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">3. The Solution: Constitutional AI as the Federal Standard</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              To resolve the conflict between "Innovation" and "Safety," we must move beyond "Human-in-the-Loop" (which is slow) to "Constitution-in-the-Code."
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">The Framework</h3>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              We propose a unified federal standard where AI models must adhere to Explicit Normative Constraints:
            </p>
            <ul className="list-disc list-inside space-y-2 mb-6 text-lg text-slate-700 ml-4">
              <li><strong>The Constitution:</strong> A set of hard-coded rules (e.g., "Do not discriminate based on protected class," "Prioritize factual accuracy over user flattery").</li>
              <li><strong>The Override:</strong> These rules must override training data. If the history says "Deny Loan," but the Constitution says "Fair Access," the Constitution wins.</li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Why this Solves the Political Gridlock</h3>
            <ul className="space-y-4 mb-6 text-lg text-slate-700">
              <li><strong>For Republicans:</strong> It frames safety as "Rule of Law" and "Accuracy," not "Social Engineering." It replaces vague "Fairness" metrics with specific "Constitutional Compliance."</li>
              <li><strong>For Democrats:</strong> It provides a hard brake on algorithmic discrimination that is more effective than "transparency" reports.</li>
              <li><strong>For Industry:</strong> It provides a single, predictable standard (The Constitution) rather than 50 state laws.</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">4. Recommendations for the 119th Congress</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">1. The "Product Safety" Pivot in the NDAA</h3>
                <p className="text-lg text-slate-700 leading-relaxed">
                  Congress should include language in the National Defense Authorization Act (NDAA) that defines AI "Safety" not as censorship, but as "Consistency and Fidelity." Models used by the federal government must demonstrate they do not mimic historical human errors.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">2. NIST RMF Update</h3>
                <p className="text-lg text-slate-700 leading-relaxed">
                  Direct NIST to update the <a href="/cai-engineering-standard" className="text-red-800 hover:text-red-900 underline cursor-pointer">AI Risk Management Framework</a> to include "Constitutional Alignment" as a technical standard for handling "Proxy Discrimination." This moves the debate from values (subjective) to constraints (objective).
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">3. The Preemption Bargain</h3>
                <p className="text-lg text-slate-700 leading-relaxed">
                  If Congress preempts state AI laws, it must replace them with a Federal Civil Rights Rider: A clause stating that no AI system may be procured that fails a "Constitutional Audit" for disparate impact.
                </p>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Conclusion</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-4">
              We are at a crossroads. We can either allow AI to become a chaotic mirror of our past mistakes, or we can engineer it to reflect our constitutional ideals. The "Third Way" is not to stop the machine, but to give it a Constitution.
            </p>
            <div className="border-l-4 border-red-800 pl-6 py-2 mt-6">
              <p className="text-lg text-slate-900 font-semibold">
                The Algorithmic Consistency Initiative, LLC
              </p>
              <p className="text-lg text-slate-700">
                Bridging Technical AI Alignment and Risk Engineering
              </p>
              <p className="text-lg text-slate-700 mt-2">
                <a href="https://AlgorithmicConsistency.org" className="text-red-800 hover:text-red-900 underline cursor-pointer">AlgorithmicConsistency.org</a>
              </p>
            </div>
          </section>

          {/* Related Reading */}
          <section className="mb-12 bg-slate-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Related Reading</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <a href="/constitutional-ai" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">Constitutional AI and Legal Accountability</h4>
                <p className="text-sm text-slate-600">A framework for auditable safety and product liability law</p>
              </a>
              <a href="/whitepaper" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">The Mirror Effect</h4>
                <p className="text-sm text-slate-600">Why AI mimics our worst habits and how to fix it</p>
              </a>
              <a href="/ai-liability" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">AI Liability Legal Framework</h4>
                <p className="text-sm text-slate-600">Why behavioral mimicry constitutes a design defect</p>
              </a>
              <a href="/cai-engineering-standard" className="block p-4 bg-white rounded-lg hover:shadow-lg transition-shadow cursor-pointer">
                <h4 className="font-bold text-slate-900 mb-2">CAI Engineering Standard</h4>
                <p className="text-sm text-slate-600">Bridging technical alignment and risk engineering</p>
              </a>
            </div>
          </section>

          {/* Call to Action */}
          <section className="mt-16 p-8 bg-slate-100 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Join the Conversation</h3>
            <p className="text-lg text-slate-700 mb-6">
              Help us bridge the gap between innovation and safety through Constitutional AI.
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-red-800 hover:bg-red-900 text-white px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get in Touch
            </a>
          </section>
        </article>

        <Footer />
      </div>
    </>
  );
}
